import React, { useEffect, useState } from 'react';
import { PipelineProject, getStage, getNextStage } from '../../types/pipeline';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from './StatusBadge';
import StagePill from './StagePill';
import ProgressBar from './ProgressBar';
import TeamPanel from './TeamPanel';
import StageHistory from './StageHistory';
import AdvanceStageModal from './AdvanceStageModal';
import EditProgramModal from './EditProgramModal';
import { formatDate } from '../../utils/dateFormat';

interface ProjectDetailPanelProps {
  project: PipelineProject | null;
  onClose: () => void;
  onProjectUpdated?: () => void;
}

type Tab = 'overview' | 'team' | 'history';


const ProjectDetailPanel: React.FC<ProjectDetailPanelProps> = ({ project, onClose, onProjectUpdated }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Reset tab when project changes
  useEffect(() => {
    setActiveTab('overview');
    setShowAdvanceModal(false);
    setShowEditModal(false);
  }, [project?.id]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Determine if current user can advance stage
  const userType = user?.user_type || 'member';
  const canAdvance = userType === 'admin' || userType === 'super_admin' || userType === 'member';
  const currentStage = project ? getStage(project.stage) : undefined;
  const nextStage = project ? getNextStage(project.stage) : undefined;
  const showAdvanceButton = canAdvance && nextStage && project?.status === 'active';

  const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'overview',
      label: 'Overview',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      key: 'team',
      label: 'Team',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
    },
    {
      key: 'history',
      label: 'History',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div
      className={`fixed top-0 right-0 h-full bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col transition-transform duration-300 ease-in-out ${
        project ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{ width: 400 }}
    >
      {project && (
        <>
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-900 truncate">{project.name}</h2>
                <p className="text-sm text-gray-500 mt-0.5">{project.target}</p>
                <p className="text-sm text-gray-400">{project.indication}</p>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors -mr-1 -mt-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <StagePill stageId={project.stage} size="md" />
              <StatusBadge status={project.status} size="md" />
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 mt-3 -mb-px">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-md border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-teal-500 text-teal-700'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {activeTab === 'overview' && (
              <>
                {/* Overall progress */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Progress</span>
                    <span className="text-sm font-semibold text-gray-700">{project.progress}%</span>
                  </div>
                  <ProgressBar value={project.progress} height="h-2.5" />
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-gray-900">{project.compounds.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Compounds</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-gray-900">{project.hits}</p>
                    <p className="text-xs text-gray-500">Hits</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-gray-900">{project.leads}</p>
                    <p className="text-xs text-gray-500">Leads</p>
                  </div>
                </div>

                {/* Milestones */}
                {project.milestones.length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Milestones</h3>
                    <div className="space-y-2">
                      {project.milestones.map((m, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                              m.done ? 'bg-emerald-500' : 'bg-gray-200'
                            }`}
                          >
                            {m.done ? (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-gray-400" />
                            )}
                          </div>
                          <span className={`text-sm ${m.done ? 'text-gray-700' : 'text-gray-400'}`}>{m.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cancellation reason */}
                {project.status === 'cancelled' && project.cancelReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h3 className="text-xs font-medium text-red-600 uppercase tracking-wide mb-1">Cancellation Reason</h3>
                    <p className="text-sm text-red-700">{project.cancelReason}</p>
                    {project.cancelDate && (
                      <p className="text-xs text-red-400 mt-1.5">Cancelled {formatDate(project.cancelDate)}</p>
                    )}
                  </div>
                )}

                {/* Notes */}
                {project.notes && (
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Latest Notes</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{project.notes}</p>
                  </div>
                )}

                {/* Details */}
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Details</h3>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Lead</span>
                    <span className="text-gray-700 font-medium">{project.lead}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Started</span>
                    <span className="text-gray-700">{formatDate(project.startDate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Last Update</span>
                    <span className="text-gray-700">{formatDate(project.lastUpdate)}</span>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'team' && (
              <TeamPanel projectId={project.id} />
            )}

            {activeTab === 'history' && (
              <StageHistory projectId={project.id} />
            )}
          </div>

          {/* Footer buttons */}
          <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
            {showAdvanceButton && currentStage && nextStage ? (
              <button
                onClick={() => setShowAdvanceModal(true)}
                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center gap-1.5"
                style={{ backgroundColor: nextStage.color }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Advance to {nextStage.short}
              </button>
            ) : (
              <button
                onClick={() => setShowEditModal(true)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Edit Program
              </button>
            )}
            <button
              onClick={() => window.location.href = `/chemistry/files?project=${project.id}`}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
            >
              View Compounds
            </button>
          </div>

          {/* Edit Program Modal */}
          {showEditModal && (
            <EditProgramModal
              project={project}
              onClose={() => setShowEditModal(false)}
              onSaved={() => {
                setShowEditModal(false);
                onProjectUpdated?.();
              }}
            />
          )}

          {/* Advance Stage Modal */}
          {showAdvanceModal && currentStage && nextStage && (
            <AdvanceStageModal
              projectId={project.id}
              projectName={project.name}
              currentStage={currentStage}
              nextStage={nextStage}
              onClose={() => setShowAdvanceModal(false)}
              onAdvanced={() => {
                setShowAdvanceModal(false);
                onProjectUpdated?.();
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProjectDetailPanel;
